import { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAdditionalTitleBarDatePicker } from '../../../../hooks/useAdditionalTitleBarComponents';
import {
  createActiveFundSelectorBySection,
  createSpecificTitleBarDatePickerSelector,
} from '../../../../redux/pages/selectors';
import { DatePicker } from '../../../../types/redux/pages/PagesStore';
import {
  formatDateForCheckingState,
  todayFormatted,
} from '../../../../utilities/dateFormatters';
import {
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../utilities/dateUtilities';
import GeneralComponentErrorShield from '../../../general/GeneralComponentErrorShield';
import GridItem from '../../../layout/GridComponents/GridItem';
import { FundFailTable } from './FailsTable';
import dayjs from 'dayjs';
import { useFails } from '../../../../api/fails/hooks/queries';
import type { FailsPayload } from '../../../../api/fails/types';
import { getRegulationBucket } from '../../../../api/fails/types';
import { useSearchParams } from 'react-router-dom';
import { updateSpecificTitleBarComponent } from '../../../../redux/pages/actions';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';

/**
 * Hook to make use of a start date and end date in the titlebar
 * @returns either undefined for both dates or the value of both
 */
export function useDatePickers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const lastSyncedValuesRef = useRef<{ start?: string; end?: string }>({});

  // Read URL params for navigation support
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');

  // Read from Redux (source of truth after initialization)
  const startDateRedux = useSelector(
    createSpecificTitleBarDatePickerSelector('startDate'),
  );
  const endDateRedux = useSelector(
    createSpecificTitleBarDatePickerSelector('endDate'),
  );

  const reduxStart = startDateRedux[0]?.currentValue;
  const reduxEnd = endDateRedux[0]?.currentValue;

  // Initialize Redux from URL params on navigation (if URL has values and Redux doesn't)
  useEffect(() => {
    if (urlStartDate && !reduxStart) {
      dispatch(
        updateSpecificTitleBarComponent(
          'startDate',
          urlStartDate,
          'datePickers',
        ),
      );
    }
    if (urlEndDate && !reduxEnd) {
      dispatch(
        updateSpecificTitleBarComponent('endDate', urlEndDate, 'datePickers'),
      );
    }
  }, [urlStartDate, urlEndDate, reduxStart, reduxEnd, dispatch]);

  // Determine initial values: Redux > URL > defaults
  const initialStart = reduxStart || urlStartDate || getWeekdayXMonthsAgo(1);
  const initialEnd = reduxEnd || urlEndDate || getMostRecentWeekday();

  // Memoize date picker configs to prevent unnecessary re-registration
  const startDatePicker: DatePicker = useMemo(
    () => ({
      titleBarKey: 'startDate',
      titleBarTitle: 'Select Start Date: ',
      currentValue: dayjs(initialStart).format('YYYY-MM-DD'),
      displayOnOverviewPage: false,
    }),
    [initialStart],
  );

  const endDatePicker: DatePicker = useMemo(
    () => ({
      titleBarKey: 'endDate',
      titleBarTitle: 'Select End Date: ',
      currentValue: dayjs(initialEnd).format('YYYY-MM-DD'),
      displayOnOverviewPage: false,
    }),
    [initialEnd],
  );

  // Register date pickers
  useAdditionalTitleBarDatePicker(startDatePicker);
  useAdditionalTitleBarDatePicker(endDatePicker);

  // Get current values from Redux (source of truth)
  const currentStart = reduxStart
    ? dayjs(reduxStart).format('YYYY-MM-DD')
    : dayjs(initialStart).format('YYYY-MM-DD');
  const currentEnd = reduxEnd
    ? dayjs(reduxEnd).format('YYYY-MM-DD')
    : dayjs(initialEnd).format('YYYY-MM-DD');

  // Sync Redux values to URL (but avoid infinite loops)
  useEffect(() => {
    // Skip if we just synced these exact values
    if (
      lastSyncedValuesRef.current.start === currentStart &&
      lastSyncedValuesRef.current.end === currentEnd
    ) {
      return;
    }

    // Read current URL params directly (not from searchParams to avoid dependency)
    const currentParams = Object.fromEntries(
      new URLSearchParams(window.location.search).entries(),
    );

    const urlStart = currentParams.startDate;
    const urlEnd = currentParams.endDate;

    // Only update if Redux values differ from URL
    if (
      currentStart &&
      currentEnd &&
      (urlStart !== currentStart || urlEnd !== currentEnd)
    ) {
      lastSyncedValuesRef.current = {
        start: currentStart,
        end: currentEnd,
      };

      setSearchParams(
        {
          ...currentParams,
          startDate: currentStart,
          endDate: currentEnd,
        },
        { replace: true },
      );
    }
  }, [currentStart, currentEnd, setSearchParams]);

  return {
    startDate: dayjs(currentStart).toDate(),
    endDate: dayjs(currentEnd).toDate(),
  };
}

function FundFailHeader({
  label,
  fundName,
  startDate,
  endDate,
}: {
  label: string;
  fundName: string;
  startDate: Date;
  endDate: Date;
}) {
  const title =
    label.toLowerCase() === 'sif-raif'
      ? 'CSSF Risk Spreading Fails'
      : `${label} Fails`;

  return (
    <div className="w-full rounded-t-md bg-white px-4 py-3">
      <h2 className="capitalize text-base font-semibold text-red-700 md:text-lg">
        {title}
        <span className="font-medium text-mainblue">
          {' '}
          — {fundName || 'Unknown Fund'}{' '}
        </span>
      </h2>
      <p className="text-sm text-slate-500">
        {startDate.toDateString()} to {endDate.toDateString()}
      </p>
    </div>
  );
}

function FailsPage({ fundId, section }: any): ReactElement {
  const { startDate, endDate } = useDatePickers();
  const details = useSelector(createActiveFundSelectorBySection(section));
  const fundName = details?.name || 'Unknown Fund';

  const startDateForFetch =
    formatDateForCheckingState(startDate) ?? todayFormatted();
  const endDateForFetch =
    formatDateForCheckingState(endDate) ?? todayFormatted();

  const failsQuery = useFails(startDateForFetch, endDateForFetch, fundId);

  return (
    <GeneralComponentErrorShield dataObjects={[]}>
      <GridItem xs={12}>
        <QueryBoundary<FailsPayload>
          isLoading={failsQuery.isLoading}
          isFetching={failsQuery.isFetching}
          isError={failsQuery.isError}
          error={failsQuery.error}
          data={failsQuery.data?.data}
          loadingMessages={['Loading Fail Data...']}
          errorMessage="Error loading fail data. Please try again later."
        >
          {(failsData) => {
            const displayFundName =
              failsData.external_fund_name || fundName;
            const regulatory = getRegulationBucket(failsData, 'regulatory');
            const prospectus = getRegulationBucket(failsData, 'prospectus');

            return (
              <div className="space-y-4">
                {section && (
                  <FundFailTable
                    title={
                      <FundFailHeader
                        label={section}
                        fundName={displayFundName}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    }
                    subject={`${section}-law`}
                    section={section}
                    fundId={fundId}
                    failsData={regulatory}
                    isFetching={failsQuery.isFetching}
                  />
                )}

                <FundFailTable
                  title={
                    <FundFailHeader
                      label="Prospectus"
                      fundName={displayFundName}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  }
                  subject="prospectus-restrictions"
                  section={section}
                  fundId={fundId}
                  failsData={prospectus}
                  isFetching={failsQuery.isFetching}
                />
              </div>
            );
          }}
        </QueryBoundary>
      </GridItem>
    </GeneralComponentErrorShield>
  );
}

export default FailsPage;
